import { clamp, range } from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';

import { useFrameListQuery } from '@/hook';
import { useTaskFrameStore } from '@/store';
import type { FrameData, Task } from '@/type';

const FRAME_RANGE_CONFIG = {
  OFFSET_START: 1,
  RANGE_SIZE: 3,
} as const;

const calculateWorkFrameRange = (
  frameSeq: number,
  fileFrameCount: number,
  startFrameSeq: number,
  workFrameInterval: number,
) => {
  const { OFFSET_START, RANGE_SIZE } = FRAME_RANGE_CONFIG;
  const start = clamp(
    frameSeq - OFFSET_START * workFrameInterval,
    startFrameSeq,
    fileFrameCount,
  );
  const end = clamp(
    start + (RANGE_SIZE - 1) * workFrameInterval,
    startFrameSeq,
    fileFrameCount,
  );

  return { start, end };
};

const filterUnloadedFrameList = (
  start: number,
  end: number,
  allFrameList: (FrameData | null)[],
  currentFrameSeq: number,
  workFrameInterval: number,
) =>
  range(start, end + 1, workFrameInterval).filter(
    seq => allFrameList[seq - 1] == null && seq !== currentFrameSeq,
  );

const useFrameRangeList = ({
  fileFrameCount,
  frameSeq,
  taskID,
}: {
  fileFrameCount: Task['fileFrameCount'];
  frameSeq: FrameData['frameSeq'];
  taskID: Task['id'];
}): void => {
  const { allFrameList, selectedTask, setAllFrameList } = useTaskFrameStore();
  const startFrameSeq = clamp(
    selectedTask?.startFrameSeq ?? 1,
    1,
    fileFrameCount,
  );
  const workFrameInterval = Math.max(selectedTask?.workFrameInterval ?? 1, 1);

  const nextFetchingFrameSeqList = useMemo(() => {
    if (!fileFrameCount || !frameSeq) return [];

    const { start, end } = calculateWorkFrameRange(
      frameSeq,
      fileFrameCount,
      startFrameSeq,
      workFrameInterval,
    );

    return filterUnloadedFrameList(
      start,
      end,
      allFrameList,
      frameSeq,
      workFrameInterval,
    );
  }, [
    allFrameList,
    fileFrameCount,
    frameSeq,
    startFrameSeq,
    workFrameInterval,
  ]);

  const { data, isLoading, error } = useFrameListQuery(
    {
      hasWorkData: true,
      hasViewOnly: true,
      taskID,
      frameSeq: nextFetchingFrameSeqList,
    },
    {
      enabled: nextFetchingFrameSeqList.length > 0,
      retry: 2,
    },
  );

  const frameList = useMemo(() => {
    if (nextFetchingFrameSeqList.length === 0 || isLoading || error) {
      return [];
    }

    return data?.frames ?? [];
  }, [nextFetchingFrameSeqList.length, isLoading, error, data?.frames]);

  const updateFrameStore = useCallback(
    (frameList: FrameData[]) => {
      if (frameList.length === 0) return;

      setAllFrameList(prevList => {
        const newList = [...prevList];

        frameList.forEach(frame => {
          const index = frame.frameSeq - 1;

          if (index >= 0 && index < newList.length) {
            newList[index] = frame;
          }
        });

        return newList;
      });
    },
    [setAllFrameList],
  );

  useEffect(() => {
    if (!isLoading && !error && frameList.length > 0) {
      updateFrameStore(frameList);
    }
  }, [frameList, isLoading, error, updateFrameStore]);

  useEffect(() => {
    if (error) {
      console.warn('Frame range list fetch error:', error);
    }
  }, [error]);
};

export default useFrameRangeList;
